import {useCallback, useRef, useState} from 'react'

const MAX_HISTORY = 30
const MEMORY_WARN_BYTES = 512 * 1024 * 1024 // 512 MB

/**
 * Undo/redo history for mask ImageData snapshots.
 * Stores snapshots of the mask canvas state.
 * Warns when estimated memory usage exceeds MEMORY_WARN_BYTES.
 */
export function useRemoveBgHistory() {
    const stackRef = useRef([])
    const indexRef = useRef(-1)
    const [revision, setRevision] = useState(0)

    const canUndo = indexRef.current > 0
    const canRedo = indexRef.current < stackRef.current.length - 1

    const warnedRef = useRef(false)

    const pushState = useCallback((imageData) => {
        const stack = stackRef.current
        const idx = indexRef.current

        // Discard any redo states
        stack.length = idx + 1

        // Trim oldest if over limit
        if (stack.length >= MAX_HISTORY) {
            stack.shift()
        }

        stack.push(imageData)
        indexRef.current = stack.length - 1
        setRevision((r) => r + 1)

        // Estimate total memory and warn once if threshold exceeded
        if (!warnedRef.current) {
            const bytesPerEntry = imageData.data.byteLength
            const totalBytes = stack.length * bytesPerEntry
            if (totalBytes > MEMORY_WARN_BYTES) {
                console.warn(
                    `[RemoveBg] History memory ~${Math.round(totalBytes / 1024 / 1024)}MB exceeds ${MEMORY_WARN_BYTES / 1024 / 1024}MB threshold (${stack.length} entries × ${Math.round(bytesPerEntry / 1024)}KB).`,
                )
                warnedRef.current = true
            }
        }
    }, [])

    const undo = useCallback(() => {
        if (indexRef.current > 0) {
            indexRef.current -= 1
            setRevision((r) => r + 1)
            return stackRef.current[indexRef.current]
        }
        return null
    }, [])

    const redo = useCallback(() => {
        if (indexRef.current < stackRef.current.length - 1) {
            indexRef.current += 1
            setRevision((r) => r + 1)
            return stackRef.current[indexRef.current]
        }
        return null
    }, [])

    const getCurrent = useCallback(() => {
        if (indexRef.current >= 0 && indexRef.current < stackRef.current.length) {
            return stackRef.current[indexRef.current]
        }
        return null
    }, [])

    const getInitial = useCallback(() => {
        if (stackRef.current.length > 0) {
            return stackRef.current[0]
        }
        return null
    }, [])

    const reset = useCallback(() => {
        stackRef.current = []
        indexRef.current = -1
        setRevision((r) => r + 1)
    }, [])

    const revertToInitial = useCallback(() => {
        if (stackRef.current.length > 0) {
            const initial = stackRef.current[0]
            stackRef.current = [initial]
            indexRef.current = 0
            setRevision((r) => r + 1)
            return initial
        }
        return null
    }, [])

    return {
        canUndo,
        canRedo,
        pushState,
        undo,
        redo,
        getCurrent,
        getInitial,
        reset,
        revertToInitial,
        revision,
    }
}
